import { NavLink, useParams } from 'react-router-dom';
import BodyWrap from '../../components/BodyWrap';
import { Drawer, Modal, Select, Text, TextInput } from '@mantine/core';
import { IconPlaylistAdd, IconWorldPlus } from '@tabler/icons-react';
import { CopyButton, GradientButton, IconButton } from '../../components/Buttons';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosError } from 'axios';
import { Project, Environment } from '../../types/Project';
import { Datablock, StandardPrompt, VariableText } from '../../types/Datablock';
import { useForm } from '@mantine/form';
import {
  StandardPromptPreview,
  VariableDataPromptPreview,
} from '../../components/DatablockPreview';
import { useNavigate } from 'react-router-dom';
import { DatablockHistoryView } from '../../components/DatablockHistoryView';
import { clearLocalStorage } from '../../utils/authUtils';
import { generateKeyFromName } from '../../utils/commonUtils';
import { ItemSelection } from '../../types/Common';

type EnvSelection = {
  value: string;
  label: string;
};

function ProjectDetail() {
  useEffect(() => {
    document.title = 'Datablocks | PromptSlate';
  }, []);
  const navigate = useNavigate();
  const { project_key: projectKey } = useParams();
  const [rightDrawerOpened, setRightDrawerOpened] = useState(false);
  const [createEnvModalOpened, setCreateEnvModalOpened] = useState(false);
  const [newEnvName, setNewEnvName] = useState('');
  const [envList, setEnvList] = useState<EnvSelection[]>([]);
  let [projectListMessage, setProjectListMessage] = useState<string>('');
  let [envCreateMessage, setEnvCreateMessage] = useState<string>('');
  let [project, setProject] = useState<Partial<Project>>({});
  let [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedEnv, setSelectedEnv] = useState<string | null>(null);
  const [selectedDatablockKey, setSelectedDatablockId] = useState<string | null>(null);
  const [generatedKey, setGeneratedKey] = useState('');
  const [environmentSelectionList, setEnvironmentSelectionList] = useState<ItemSelection[]>([]);

  const openRightDrawer = (key: string) => {
    setSelectedDatablockId(key);
    setRightDrawerOpened(true);
  };

  const closeRightDrawer = () => {
    setRightDrawerOpened(false);
    setSelectedDatablockId(null);
  };

  useEffect(() => {
    const authToken = localStorage.getItem('authToken');
    var headers = {
      headers: { Authorization: 'Bearer ' + authToken },
    };
    axios
      .get(`${process.env.REACT_APP_API_URL}/project/${projectKey}`, headers)
      .then((res) => {
        setProject(res.data as Project);
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch project details');
          } else {
            setProjectListMessage('A network error occured. Please try again later.');
          }
        } else {
          setProjectListMessage('An error occured. Please try again later.');
        }
      });
  }, []);

  const {
    error,
    data: envData,
    refetch: refetchEnvs,
  } = useQuery({
    queryKey: [`envList-${projectKey}`],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');

        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/project/${projectKey}/environment`,
            {
              headers: headers,
            }
          );
          const environments: Environment[] = response.data.environments;
          const envSelectData = environments.map((env) => ({
            value: env.key,
            label: env.name,
          }));
          setEnvList(envSelectData);
          setSelectedEnv(envSelectData[0].value);
          return environments;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch environments');
            return [];
          } else {
            setProjectListMessage('A network error occured. Please try again later.');
            return [];
          }
        } else {
          setProjectListMessage('An error occured. Please try again later.');
          return [];
        }
      }
    },
  });

  const {
    error: datablockFetchError,
    data: datablockData,
    refetch: refetchDatablocks,
  } = useQuery({
    queryKey: [`datablocks-${projectKey}`],
    refetchOnWindowFocus: false,
    queryFn: async () => {
      try {
        const authToken = localStorage.getItem('authToken');
        if (authToken) {
          const headers = {
            Authorization: 'Bearer ' + authToken,
          };
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/project/${projectKey}/datablock`,
            {
              headers: headers,
            }
          );
          const datablocks: Datablock[] = response.data.datablocks;
          return datablocks;
        } else {
          return [];
        }
      } catch (error) {
        if (axios.isAxiosError(error)) {
          const axiosError = error as AxiosError;
          if (axiosError.status === 401) {
            clearLocalStorage();
            navigate('/login');
          }
          if (axiosError.code === 'ERR_BAD_REQUEST') {
            setProjectListMessage('Unable to fetch datablocks');
            return [];
          } else {
            setProjectListMessage('A network error occured. Please try again later.');
            return [];
          }
        } else {
          setProjectListMessage('An error occured. Please try again later.');
          return [];
        }
      }
    },
  });

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`linklet.com/`);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleCreateEnvModalToggle = (opened: boolean) => {
    if (!projectKey) {
      return;
    }
    fetchEnvironments(projectKey);
    setNewEnvName('');
    setGeneratedKey('');
    createEnvForm.reset();
    setCreateEnvModalOpened(opened);
  };

  const createEnvForm = useForm({
    initialValues: {
      newEnvName: '',
      sourceEnvironmentKey: '',
    },

    validate: {
      newEnvName: (value: string) => {
        if (!value) {
          return 'Enter an environment name';
        }
      },
      sourceEnvironmentKey: (value: string) => {
        // Only allow env creation without source if its the first environment
        if (envList.length > 0 && !value) {
          return 'Select a source environment';
        }
      },
    },
  });

  const fetchEnvironments = async (projectKey: string) => {
    try {
      const authToken = localStorage.getItem('authToken');

      if (authToken) {
        const headers = {
          Authorization: 'Bearer ' + authToken,
        };
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/project/${projectKey}/environment`,
          {
            headers: headers,
          }
        );
        const environments: Environment[] = response.data.environments;
        const envSelectData = environments.map((environment) => ({
          value: environment.key,
          label: environment.name,
        }));
        setEnvironmentSelectionList(envSelectData);
      } else {
        return [];
      }
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setProjectListMessage('Unable to fetch projects');
          return [];
        } else {
          setProjectListMessage('A network error occured. Please try again later.');
          return [];
        }
      } else {
        setProjectListMessage('An error occured. Please try again later.');
        return [];
      }
    }
  };

  const handleCreateEnvSubmit = async (values: any) => {
    setEnvCreateMessage('');
    setIsLoading(true);
    try {
      const authToken = localStorage.getItem('authToken');
      const headers = {
        Authorization: 'Bearer ' + authToken,
      };
      await axios.post(
        `${process.env.REACT_APP_API_URL}/environment/create`,
        {
          project_key: projectKey,
          name: values.newEnvName,
          env_key: generatedKey,
          source_env_key: values.sourceEnvironmentKey,
        },
        {
          headers: headers,
        }
      );
      values.newEnvName = '';
      refetchEnvs();
      refetchDatablocks();
      setIsLoading(false);
      setCreateEnvModalOpened(false);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        if (axiosError.status === 401) {
          clearLocalStorage();
          navigate('/login');
        }
        if (axiosError.code === 'ERR_BAD_REQUEST') {
          setEnvCreateMessage('Invalid project name');
        } else {
          setEnvCreateMessage('A network error occured. Please try again later.');
        }
      } else {
        setEnvCreateMessage('An error occured. Please try again later');
      }
      setIsLoading(false);
    }
  };

  return (
    <BodyWrap>
      <Modal
        opened={createEnvModalOpened}
        onClose={() => handleCreateEnvModalToggle(false)}
        title={<Text size={'xl'}>Create an environment</Text>}
        size={'lg'}
        radius="lg"
      >
        <form
          noValidate
          onSubmit={createEnvForm.onSubmit((values) => handleCreateEnvSubmit(values))}
          className="flex flex-col gap-5 pl-5 pr-5"
        >
          <TextInput
            className="mt-5"
            size="md"
            placeholder="Name of new environment"
            {...createEnvForm.getInputProps('newEnvName')}
            onChange={(e) => {
              const name = e.currentTarget.value;
              createEnvForm.setFieldValue('newEnvName', name);
              setGeneratedKey(generateKeyFromName(name));
            }}
          />
          <TextInput
            className="mt-5"
            size="md"
            placeholder="Environment Key"
            value={generatedKey}
            disabled
          />
          {envList.length > 0 ? (
            <Select
              placeholder="Select a source environment"
              data={environmentSelectionList}
              value={createEnvForm.values.sourceEnvironmentKey}
              onChange={(value) => {
                if (value) {
                  createEnvForm.setFieldValue('sourceEnvironmentKey', value);
                }
              }}
              allowDeselect={false}
              className="w-full mt-5 text-lg"
              styles={{
                input: {
                  height: 45,
                  paddingTop: 6,
                  paddingBottom: 6,
                },
              }}
            />
          ) : (
            <span />
          )}

          <GradientButton className="mt-2 mb-5" type="submit">
            Create environment
          </GradientButton>
        </form>
      </Modal>
      <Drawer
        offset={8}
        radius="md"
        opened={rightDrawerOpened}
        onClose={closeRightDrawer}
        title="Datablock version history"
        position="right"
      >
        {selectedDatablockKey && projectKey && selectedEnv ? (
          <DatablockHistoryView
            projectKey={projectKey}
            selectedEnv={selectedEnv}
            datablockKey={selectedDatablockKey}
            refetchDatablocks={refetchDatablocks}
          />
        ) : (
          <div />
        )}
      </Drawer>
      {project && (
        <div>
          <div className="text-xl pb-5">
            <div className="flex flex-row">
              <NavLink to={`/dashboard`}>Projects&nbsp;</NavLink> / {project.name}
              {project.key ? <CopyButton data={project.key} title="Copy datablock key" /> : <div />}
            </div>
          </div>
          <div className="flex flex-row justify-between w-full ">
            <div className="flex flex-row">
              <Select
                placeholder="Select an environment"
                data={envList}
                defaultValue={selectedEnv}
                value={selectedEnv}
                onChange={(value) => setSelectedEnv(value)}
                allowDeselect={false}
                className="w-[300px]"
              />
              {selectedEnv ? (
                <CopyButton data={selectedEnv} title="Copy environment key" className="h-[35px]" />
              ) : (
                <span />
              )}
              <IconButton
                title="Create a new environment"
                className="h-[35px] ml-2 text-slate-600 hover:text-slate-800"
                onClick={() => handleCreateEnvModalToggle(true)}
              >
                <IconWorldPlus size="1.5rem" />
              </IconButton>
            </div>

            <NavLink to={`/project/${projectKey}/datablock/build`}>
              <IconButton
                title={envList.length == 0 ? 'Create an environment first' : 'Create Datablock'}
                disabled={envList.length == 0}
              >
                <IconPlaylistAdd size="1.5rem" className="mr-2" /> Create Datablock
              </IconButton>
            </NavLink>
          </div>
          <div className="flex flex-col mt-8 gap-6">
            {selectedEnv && datablockData ? (
              datablockData.map((datablock) => {
                if (datablock.environment_key == selectedEnv) {
                  switch (datablock.type) {
                    case 'STANDARD_PROMPT':
                      return (
                        <StandardPromptPreview
                          key={datablock.key}
                          name={datablock.name}
                          envKey={selectedEnv}
                          datablockKey={datablock.key}
                          role={(datablock.data as StandardPrompt).role}
                          prompt={(datablock.data as StandardPrompt).prompt}
                          actions={{
                            onVersionClick: () => openRightDrawer(datablock.key),
                            showActions: true,
                          }}
                        />
                      );
                    case 'VARIABLE_DATA':
                      return (
                        <VariableDataPromptPreview
                          key={datablock.key}
                          name={datablock.name}
                          envKey={selectedEnv}
                          datablockKey={datablock.key}
                          variable={(datablock.data as VariableText).variable}
                          actions={{
                            onVersionClick: () => openRightDrawer(datablock.key),
                            showActions: true,
                          }}
                        />
                      );
                    default:
                      return null;
                  }
                }
              })
            ) : selectedEnv ? (
              <div className="w-full flex justify-center text-slate-600 ">
                Datablocks will appear here once created
              </div>
            ) : (
              <div className="w-full flex justify-center text-slate-600 ">
                Select an environment to view Datablocks
              </div>
            )}
          </div>
        </div>
      )}
    </BodyWrap>
  );
}

export default ProjectDetail;
